import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { ampScripts } from "@redotech/redo-email-content/email-wrapper";
import { EmailFormat } from "@redotech/redo-marketing-templates-common/email-builder";
import { RedoMerchantEmailTemplatesRpcClientContext } from "@redotech/redo-merchant-app-common/email-templates-rpc-client-provider";
import { EmailBlockIframeIsolator } from "@redotech/redo-web-email-builder/email-builder/builder/email-block-isolator";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import * as styles from "../send-email.module.css";

export interface EmailPreviewContentProps {
  templateId?: string;
  originTemplateId?: string;
  onClick?: () => void;
  scale?: number;
  fullPreview?: boolean;
}

export const EmailPreviewContent = memo(function EmailPreviewContent({
  templateId,
  originTemplateId,
  onClick,
  scale = 0.5,
  fullPreview = false,
}: EmailPreviewContentProps) {
  const marketingRpcClient = useRequiredContext(
    RedoMerchantEmailTemplatesRpcClientContext,
  );

  const previewLoad = useLoad(async () => {
    if (templateId) {
      return await marketingRpcClient.previewEmailTemplate({ templateId });
    }
    if (originTemplateId) {
      return await marketingRpcClient.previewEmailTemplate({
        originTemplateId,
      });
    }
    return null;
  }, [templateId, originTemplateId, marketingRpcClient]);

  const { pending: isLoading, error, value: data } = previewLoad;

  if (isLoading) {
    return (
      <Flex align="center" justify="center" p="lg" w="full">
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  if (error || !data) {
    return (
      <Flex align="center" justify="center" p="lg" w="full">
        <Text fontSize="xs" textAlign="center" textColor="secondary">
          Could not load preview
        </Text>
      </Flex>
    );
  }

  if (fullPreview) {
    return (
      <EmailBlockIframeIsolator
        format={EmailFormat.STATIC}
        includeWrapperPadding
        rawHtml={data}
        scriptUrls={ampScripts}
      />
    );
  }

  return (
    <Flex
      overflow="hidden"
      position="relative"
      style={{ maxHeight: "300px" }}
      w="full"
    >
      <div className={styles.emailBlockLink} onClick={onClick}>
        <EmailBlockIframeIsolator
          format={EmailFormat.STATIC}
          includeWrapperPadding
          rawHtml={data}
          scale={scale}
          scriptUrls={ampScripts}
        />
      </div>
    </Flex>
  );
});
